/**
 * Iter-157A Phase 5 — Restore to a Copy dialog.
 *
 * Iter-157P.18.4 — Naming UX refined:
 *   • The user types the EXACT name they want for the restored
 *     project. No automatic prefix, no automatic suffix, no live
 *     "we'll call it X" composition — what the user types is what
 *     the project list shows.
 *   • If the user leaves the name blank, we generate a clear
 *     timestamped default:
 *         "<Source> — Restored Copy — 15 Jun 2026 12:18"
 *   • Restore lineage (where did this copy come from?) is preserved
 *     and surfaced via the existing `RestoreLineageBanner` on the
 *     project page, NOT mashed into the project's name.
 *
 * Producer-facing rationale: a production may create multiple
 * restored copies over time. If they're all called "Meantime",
 * the producer cannot tell them apart. The default name carries
 * the timestamp so consecutive copies never collide; the user is
 * free to type a meaningful name (e.g. "Pre Pink Revision") and
 * have it land in their workspace exactly as typed.
 *
 * Optional reason field goes ONLY into the audit log — it does
 * not appear in the project name.
 */

import { useEffect, useState } from "react";
import { Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";


/** Iter-157P.18.4 — producer-facing timestamp: "15 Jun 2026 12:18". */
function humanStamp() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return (
        `${pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
}


function defaultRestoreName(sourceName) {
    const src = (sourceName || "").trim() || "Untitled";
    return `${src} — Restored Copy — ${humanStamp()}`;
}


export default function AtticCloneDialog({
    open,
    onClose,
    project,
    rp,
    onCloned,
}) {
    const apiBase = useApiBase();
    const [name, setName] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const projectName = (project?.name || "").trim() || "Untitled";

    useEffect(() => {
        if (!open) return;
        // Pre-fill with the timestamped default so consecutive
        // restored copies don't collide on display. The user can
        // overwrite this with their own name if they prefer.
        setName(defaultRestoreName(projectName));
        setReason("");
    }, [open, rp?.id, projectName]);

    if (!rp) return null;

    const handleClone = async () => {
        // Iter-157P.18.4 — defensive duplicate-click guard.
        // Iter-157P.18.8 — full polling-bridge mirror of revision
        // upload + restore-point create. The Cloudflare ~100s edge
        // cap kills the clone POST for large source projects
        // (Meantime hit this). The backend completes successfully
        // — the copy is in the projects list with the right name —
        // but axios threw and the producer was left staring at a
        // raw timeout. Same bridge pattern: 120s timeout, snapshot
        // pre-submit project ids, on failure poll /projects every
        // 5s for up to 3 minutes looking for the new copy, treat
        // its appearance as success and navigate automatically.
        if (submitting) return;
        setSubmitting(true);
        const finalName = (name || "").trim() || defaultRestoreName(projectName);

        // Snapshot pre-submit project ids so we can detect the new
        // copy even if the POST failed at the edge.
        let knownIds = new Set();
        try {
            const { data } = await api.get(apiBase, { timeout: 15000 });
            const list = Array.isArray(data) ? data : [];
            knownIds = new Set(list.map((p) => p.id));
        } catch {
            /* pre-snapshot best-effort; absence won't break the bridge */
        }

        const pollForNewCopy = async () => {
            const MAX_ATTEMPTS = 36;  // 36 × 5s = 3 minutes
            const POLL_MS = 5000;
            for (let i = 0; i < MAX_ATTEMPTS; i++) {
                try {
                    const { data } = await api.get(apiBase, { timeout: 15000 });
                    const list = Array.isArray(data) ? data : [];
                    // Primary signal — any id absent from the
                    // pre-submit set. Secondary — match by the
                    // producer-chosen final name (most robust:
                    // P.18.7 confirmed the name persists).
                    const byNewId = list.find((p) => !knownIds.has(p.id));
                    const byName = list.find((p) => p.name === finalName && !knownIds.has(p.id));
                    const recovered = byName || byNewId;
                    if (recovered) return recovered;
                } catch {
                    /* swallow — keep polling */
                }
                await new Promise((resolve) => setTimeout(resolve, POLL_MS));
            }
            return null;
        };

        const finaliseSuccess = (newProjectMeta) => {
            toast.success(
                `Copy created · ${newProjectMeta?.name || finalName}`,
                {
                    description:
                        "Your new project is ready in the workspace. The original is untouched.",
                    duration: 8000,
                },
            );
            // onCloned navigates the producer into the restored copy
            // (both call sites already implement this). For the
            // recovery branch we synthesise the same shape the
            // happy-path returns.
            onCloned?.({
                new_project_id: newProjectMeta?.id || newProjectMeta?.new_project_id,
                new_project: newProjectMeta,
            });
            onClose?.();
        };

        try {
            const res = await api.post(
                `${apiBase}/${project.id}/restore-points/${rp.id}/clone-to-new-project`,
                { name: finalName, reason: reason.trim() || null },
                { timeout: 120000 },
            );
            finaliseSuccess(res.data?.new_project);
        } catch (err) {
            // Polling bridge — backend may still be finishing the
            // clone server-side while the edge already cut us off.
            const recovered = await pollForNewCopy();
            if (recovered) {
                finaliseSuccess(recovered);
            } else {
                // True failure. Persistent plain-English message —
                // never raw Cloudflare/origin wording.
                toast.error(
                    "We couldn't restore this copy.",
                    {
                        description:
                            "Your original project is safe — nothing was changed. The new copy did not appear in your workspace after a 3-minute wait. Please try again in a moment.",
                        duration: Infinity,
                    },
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose?.()}>
            <DialogContent
                data-testid="attic-clone-dialog"
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Copy size={16} className="text-sky-300" />
                        Restore to a copy
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400 text-sm leading-relaxed">
                        We&apos;ll create a brand-new project from this point.
                        The original <span className="text-neutral-200">{projectName}</span> stays
                        completely untouched.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {submitting ? (
                        <div
                            data-testid="attic-clone-banner"
                            className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 flex items-start gap-3"
                        >
                            <Loader2
                                size={14}
                                className="text-amber-200 animate-spin shrink-0 mt-0.5"
                            />
                            <div className="flex-1 text-[12px] text-amber-50 leading-relaxed">
                                <div className="font-semibold">
                                    Creating restored copy…
                                </div>
                                <div className="text-amber-100/85 mt-0.5">
                                    Large projects can take a moment. Please keep
                                    this dialog open — we&apos;ll open the new copy
                                    automatically when it&apos;s ready. No need to
                                    refresh.
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div>
                        <label
                            htmlFor="attic-clone-name"
                            className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5"
                        >
                            Project name
                        </label>
                        <Input
                            id="attic-clone-name"
                            data-testid="attic-clone-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={defaultRestoreName(projectName)}
                            maxLength={180}
                            disabled={submitting}
                        />
                        <p className="text-[11px] text-neutral-500 mt-1.5 leading-relaxed">
                            What should this restored copy be called? Type whatever
                            makes it easy to spot in your workspace later. Leave it
                            blank and we&apos;ll use the timestamped default.
                        </p>
                    </div>

                    <div>
                        <label
                            htmlFor="attic-clone-reason"
                            className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5"
                        >
                            Optional note for the audit log
                        </label>
                        <Textarea
                            id="attic-clone-reason"
                            data-testid="attic-clone-reason-input"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Anything you want future readers to know."
                            maxLength={400}
                            rows={2}
                            disabled={submitting}
                            className="resize-none"
                        />
                        <p className="text-[11px] text-neutral-500 mt-1.5 leading-relaxed">
                            This note goes to the audit log only. It does not appear
                            in the project name.
                        </p>
                    </div>

                    {/* Iter-157P.18.4 — calm lineage hint so the producer
                        understands the restored copy will carry a visible
                        "Restored from {source}" banner inside the project
                        regardless of what they name it. */}
                    <div
                        data-testid="attic-clone-lineage-hint"
                        className="rounded-md border border-white/10 bg-secondary/40 px-3 py-2.5"
                    >
                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1">
                            Lineage preserved automatically
                        </div>
                        <div className="text-[12px] text-neutral-200 leading-snug">
                            Inside the restored copy you&apos;ll see a calm banner
                            reading <span className="text-neutral-100 font-medium">
                                Restored from {projectName} · {humanStamp()}
                            </span>{" "}
                            so future-you always knows where it came from.
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        data-testid="attic-clone-cancel"
                        onClick={() => !submitting && onClose?.()}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        data-testid="attic-clone-confirm"
                        onClick={handleClone}
                        disabled={submitting}
                        className="font-semibold"
                    >
                        {submitting ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" />
                                Creating copy…
                            </span>
                        ) : (
                            "Create copy"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
